import {
  ArrowDown,
  ArrowUpDown,
  ChevronDown,
  Info,
  RotateCcw,
  Search,
} from 'lucide-react'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { Pagination } from '../components/Pagination'
import {
  fetchJdWarehouseSlowMoving,
  type JdWarehouseJudgementKey,
  type JdWarehouseSlowLabel,
  type JdWarehouseSlowMovingData,
  type JdWarehouseSlowMovingProduct,
  type JdWarehouseSlowMovingWarehouse,
} from '../jdWarehouseSlowMoving'

type JudgementFilter = 'all' | JdWarehouseJudgementKey
type LabelFilter = 'all' | JdWarehouseSlowLabel
type SalesFilter = 'all' | '0-3' | '4-29' | '30+'
type SortKey = 'risk' | 'stock-desc' | 'sales-asc' | 'support-desc' | 'cost-desc'

interface Filters {
  search: string
  judgement: JudgementFilter
  label: LabelFilter
  warehouse: string
  sales: SalesFilter
  sort: SortKey
}

const EMPTY_FILTERS: Filters = {
  search: '', judgement: 'all', label: 'all', warehouse: 'all', sales: 'all', sort: 'risk',
}
const JUDGEMENT_ORDER: Record<JdWarehouseJudgementKey, number> = {
  'true-slow': 0, 'high-stock': 1, allocation: 2, review: 3,
}
const LABEL_CLASS: Record<JdWarehouseSlowLabel, string> = {
  严重滞销: 'is-severe', 轻度滞销: 'is-mild', 无销售: 'is-none',
}
const integer = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 })
const yuan = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 })
const decimal = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 })

function productMatches(product: JdWarehouseSlowMovingProduct, filters: Filters) {
  const needle = filters.search.trim().toLowerCase()
  if (needle) {
    const text = [product.code, product.name, product.sourceName, ...product.warehouses.map((row) => row.warehouse)]
      .join(' ')
      .toLowerCase()
    if (!text.includes(needle)) return false
  }
  if (filters.judgement !== 'all' && product.judgementKey !== filters.judgement) return false
  if (filters.label !== 'all' && !product.labels.includes(filters.label)) return false
  if (filters.warehouse !== 'all' && !product.warehouses.some((row) => row.warehouse === filters.warehouse)) return false
  if (filters.sales === '0-3' && product.sales30 > 3) return false
  if (filters.sales === '4-29' && (product.sales30 < 4 || product.sales30 > 29)) return false
  if (filters.sales === '30+' && product.sales30 < 30) return false
  return true
}

function compareProducts(left: JdWarehouseSlowMovingProduct, right: JdWarehouseSlowMovingProduct, sort: SortKey) {
  if (sort === 'stock-desc') return right.stock - left.stock || left.code.localeCompare(right.code)
  if (sort === 'sales-asc') return left.sales30 - right.sales30 || right.stock - left.stock
  if (sort === 'support-desc') return (right.supportDays ?? -1) - (left.supportDays ?? -1) || right.stock - left.stock
  if (sort === 'cost-desc') return (right.occupiedCost ?? -1) - (left.occupiedCost ?? -1) || right.stock - left.stock
  const judgementDifference = JUDGEMENT_ORDER[left.judgementKey] - JUDGEMENT_ORDER[right.judgementKey]
  if (judgementDifference) return judgementDifference
  if (left.judgementKey === 'allocation') {
    return right.sales30 - left.sales30 || right.stock - left.stock || left.code.localeCompare(right.code)
  }
  return (right.supportDays ?? 0) - (left.supportDays ?? 0)
    || right.stock - left.stock
    || left.code.localeCompare(right.code)
}

function recentSale(product: JdWarehouseSlowMovingProduct) {
  if (product.daysSinceLastSale === null) return '可查期内无成交'
  if (product.daysSinceLastSale === 0) return '今日有成交'
  return `${integer.format(product.daysSinceLastSale)}天前`
}

function supportDays(product: JdWarehouseSlowMovingProduct) {
  return product.supportDays === null ? '—' : `${integer.format(Math.round(product.supportDays))}天`
}

function WarehouseLabels({ product }: { product: JdWarehouseSlowMovingProduct }) {
  return (
    <div className="jd-slow-label-list">
      {product.labels.map((label) => (
        <span className={`jd-slow-warehouse-label ${LABEL_CLASS[label]}`} key={label}>
          {label} {integer.format(product.labelStock[label])}
        </span>
      ))}
    </div>
  )
}

function historyText(values: number[]) {
  return values.map((value) => decimal.format(value)).join(' / ')
}

function WarehouseDetail({ product }: { product: JdWarehouseSlowMovingProduct }) {
  return (
    <tr className="jd-slow-detail-row">
      <td colSpan={11}>
        <div className="jd-slow-warehouse-panel">
          <header>
            <h3>涉及仓库明细（{product.warehouseCount}个）</h3>
            <p>京东物流仓级口径；全店成交仅用于辅助判断，不分摊到仓库。</p>
          </header>
          <div className="jd-slow-warehouse-scroll">
            <table className="jd-slow-warehouse-table">
              <thead>
                <tr>
                  <th>#</th><th>仓库名称</th><th>仓库标签</th><th className="numeric">现货（件）</th>
                  <th className="numeric">在途（件）</th><th className="numeric">预计可售（天）</th>
                  <th>历史销量（7 / 14 / 28日）</th><th>预测销量（7 / 14 / 28日）</th>
                  <th>首次进入滞销</th><th>连续滞销天数</th>
                </tr>
              </thead>
              <tbody>
                {product.warehouses.map((row, index) => <WarehouseRow row={row} index={index} key={`${row.warehouse}-${row.label}-${row.jdLogisticsProductCode}-${index}`} />)}
              </tbody>
            </table>
          </div>
        </div>
      </td>
    </tr>
  )
}

function WarehouseRow({ row, index }: { row: JdWarehouseSlowMovingWarehouse; index: number }) {
  return (
    <tr>
      <td>{index + 1}</td>
      <td className="jd-slow-warehouse-name">{row.warehouse}</td>
      <td><span className={`jd-slow-warehouse-label ${LABEL_CLASS[row.label]}`}>{row.label}</span></td>
      <td className="numeric">{integer.format(row.spot)}</td>
      <td className="numeric">{integer.format(row.innerTransit + row.purchaseTransit)}</td>
      <td className="numeric">{row.expectedDays >= 999 ? '999+' : integer.format(row.expectedDays)}</td>
      <td>{historyText(row.historyTotal)}</td>
      <td>{historyText(row.forecastTotal)}</td>
      <td>{row.firstEnteredSlowDate || '待积累快照'}</td>
      <td>{row.continuousSlowDays === null ? '待积累' : `${integer.format(row.continuousSlowDays)}天`}</td>
    </tr>
  )
}

function ProductRow({
  product,
  index,
  expanded,
  onToggle,
}: {
  product: JdWarehouseSlowMovingProduct
  index: number
  expanded: boolean
  onToggle: (code: string) => void
}) {
  return (
    <>
      <tr className={`jd-slow-product-row${expanded ? ' is-expanded' : ''}`}>
        <td className="jd-slow-index">{index + 1}</td>
        <td>
          <div className="jd-slow-product-identity">
            {product.image
              ? <img src={product.image} alt="" />
              : <span className="jd-slow-image-fallback">暂无图</span>}
            <span>
              <strong>{product.code}</strong>
              <span title={product.name}>{product.name}</span>
              <small>{product.jdSkuCount}个京东SKU</small>
            </span>
          </div>
        </td>
        <td><span className={`jd-slow-judgement is-${product.judgementKey}`} title={product.judgementNote}>{product.judgement}</span></td>
        <td className="numeric"><strong>{integer.format(product.stock)}</strong> 件</td>
        <td><WarehouseLabels product={product} /></td>
        <td className="numeric">{integer.format(product.warehouseCount)} 仓</td>
        <td className="numeric"><strong>{integer.format(product.sales30)}</strong> 件<small>¥{yuan.format(product.gmv30)}</small></td>
        <td className="numeric">{recentSale(product)}<small>{product.lastSale || '—'}</small></td>
        <td className="numeric">{supportDays(product)}<small>滞销仓现货÷全店日均</small></td>
        <td className="numeric">{product.occupiedCost === null ? '—' : `¥${yuan.format(product.occupiedCost)}`}<small>{product.cost === null ? '缺成本' : `成本¥${yuan.format(product.cost)}/件`}</small></td>
        <td className="jd-slow-expand-cell">
          <button type="button" aria-expanded={expanded} aria-label={`${expanded ? '收起' : '展开'}${product.code}仓库明细`} onClick={() => onToggle(product.code)}>
            <ChevronDown aria-hidden="true" />
          </button>
        </td>
      </tr>
      {expanded ? <WarehouseDetail product={product} /> : null}
    </>
  )
}

function Metric({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return <div><span>{label}</span><strong>{value}{suffix ? <small>{suffix}</small> : null}</strong></div>
}

function SourceAndLogic({ data }: { data: JdWarehouseSlowMovingData }) {
  const { thresholds, summary } = data
  return (
    <section className="jd-slow-method" aria-labelledby="jd-slow-method-title">
      <h2 id="jd-slow-method-title">判断逻辑与数据边界</h2>
      <div>
        <article><h3>真正滞销候选</h3><p>全店近30天销量不超过{thresholds.trueSlowMaxSales30}件，并且距最近一次成交至少{thresholds.trueSlowMinDaysSinceLastSale}天。此类优先复核清仓、退仓或停止补货，不自动执行任何后台动作。</p></article>
        <article><h3>区域错配与高库存观察</h3><p>全店销量至少{thresholds.allocationMinSales30}件但部分仓无销售/严重滞销，标为区域错配；滞销仓现货÷全店近30天日均销量达到{thresholds.highStockMinSupportDays}天，标为高库存观察。</p></article>
        <article><h3>当前不能回答的字段</h3><p>{data.snapshotHistory.continuousMetricsReady ? '已开始按日积累快照；仓级首次进入和连续滞销天数按连续完整快照计算。' : '目前只有单日快照，首次进入滞销和连续滞销天数显示为待积累；至少需要连续两天完整快照。'}</p></article>
      </div>
      <p className="jd-slow-source-line">来源：{data.snapshotDate}京东物流三类滞销明细（{summary.rowCount}条产品×仓库记录）｜京东销售窗口 {data.salesStart} 至 {data.salesEnd}｜图片、销售与成本关联 {summary.mappedProductCount}/{summary.imageProductCount}/{summary.costProductCount}款。库存价值字段原表均为0，本页占用成本按台账产品成本估算。</p>
    </section>
  )
}

export function JdWarehouseSlowMovingPage() {
  const [data, setData] = useState<JdWarehouseSlowMovingData | null>(null)
  const [error, setError] = useState('')
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS)
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => {
    const controller = new AbortController()
    fetchJdWarehouseSlowMoving(controller.signal)
      .then((payload) => {
        setData(payload)
        setExpanded((current) => current.size ? current : new Set(payload.products.slice(0, 1).map((product) => product.code)))
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : String(reason))
      })
    return () => controller.abort()
  }, [])

  const warehouses = useMemo(() => data
    ? [...new Set(data.products.flatMap((product) => product.warehouses.map((row) => row.warehouse)))].sort((left, right) => left.localeCompare(right, 'zh-CN'))
    : [], [data])

  const filtered = useMemo(() => {
    if (!data) return []
    return data.products.filter((product) => productMatches(product, filters)).sort((left, right) => compareProducts(left, right, filters.sort))
  }, [data, filters])

  if (error) return <div className="error-state"><h1>京东仓滞销数据加载失败</h1><p>{error}</p></div>
  if (!data) return <div className="loading-state">正在加载京东仓滞销数据…</div>

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const start = (currentPage - 1) * pageSize
  const paged = filtered.slice(start, start + pageSize)
  const filteredStock = filtered.reduce((total, product) => total + product.stock, 0)
  const filteredCost = filtered.reduce((total, product) => total + (product.occupiedCost || 0), 0)

  const submitFilters = (event: FormEvent) => {
    event.preventDefault()
    setFilters(draft)
    setPage(1)
  }
  const resetFilters = () => {
    setDraft(EMPTY_FILTERS)
    setFilters(EMPTY_FILTERS)
    setExpanded(new Set(data.products.slice(0, 1).map((product) => product.code)))
    setPage(1)
  }
  const applySort = (sort: SortKey) => {
    setDraft((current) => ({ ...current, sort }))
    setFilters((current) => ({ ...current, sort }))
    setPage(1)
  }
  const toggleProduct = (code: string) => {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  return (
    <div className="page-stack jd-warehouse-page">
      <header className="page-title-row">
        <div><h1>京东仓滞销分析</h1><p>京东物流滞销快照 × 台账京东近30天成交 × 公司产品成本；库存仅覆盖进入三类预警的仓库。</p></div>
        <DataFreshnessBadge date={data.snapshotDate} label="数据截至" mode="on-demand" />
      </header>

      <section className="jd-slow-summary" aria-label="滞销库存概览">
        <Metric label="滞销仓现货" value={integer.format(data.summary.stock)} suffix="件" />
        <Metric label="涉及产品" value={integer.format(data.summary.productCount)} suffix="款" />
        <Metric label="无销售仓库存" value={integer.format(data.summary.labelStock['无销售'])} suffix="件" />
        <Metric label="严重滞销库存" value={integer.format(data.summary.labelStock['严重滞销'])} suffix="件" />
        <Metric label="预计占用成本" value={`¥${yuan.format(data.summary.occupiedCost)}`} />
        <Metric label="全店近30天销量" value={integer.format(data.summary.sales30)} suffix="件" />
      </section>

      <div className="jd-slow-scope-note"><Info aria-hidden="true" /><span>仓库标签不等于全店滞销：<strong>全部{data.summary.soldProductCount}款近30天均有成交</strong>。优先区分产品需求问题与区域仓间错配。</span></div>

      <form className="jd-slow-filter-form" onSubmit={submitFilters}>
        <label className="jd-slow-search"><span>商品搜索</span><span><Search aria-hidden="true" /><input value={draft.search} onChange={(event) => setDraft((current) => ({ ...current, search: event.target.value }))} placeholder="搜索商品名称、产品编码或仓库" /></span></label>
        <label><span>判断分类</span><select value={draft.judgement} onChange={(event) => setDraft((current) => ({ ...current, judgement: event.target.value as JudgementFilter }))}><option value="all">全部</option><option value="true-slow">真正滞销候选</option><option value="high-stock">高库存观察</option><option value="allocation">区域错配</option><option value="review">仓库预警复核</option></select></label>
        <label><span>仓库标签</span><select value={draft.label} onChange={(event) => setDraft((current) => ({ ...current, label: event.target.value as LabelFilter }))}><option value="all">全部</option><option value="严重滞销">严重滞销</option><option value="轻度滞销">轻度滞销</option><option value="无销售">无销售</option></select></label>
        <label><span>仓库</span><select value={draft.warehouse} onChange={(event) => setDraft((current) => ({ ...current, warehouse: event.target.value }))}><option value="all">全部</option>{warehouses.map((warehouse) => <option value={warehouse} key={warehouse}>{warehouse}</option>)}</select></label>
        <label><span>近30天销量</span><select value={draft.sales} onChange={(event) => setDraft((current) => ({ ...current, sales: event.target.value as SalesFilter }))}><option value="all">全部</option><option value="0-3">0–3件</option><option value="4-29">4–29件</option><option value="30+">30件及以上</option></select></label>
        <label><span>排序</span><select value={draft.sort} onChange={(event) => setDraft((current) => ({ ...current, sort: event.target.value as SortKey }))}><option value="risk">风险优先</option><option value="stock-desc">滞销仓现货从高到低</option><option value="sales-asc">近30天销量从低到高</option><option value="support-desc">参考支撑天数从高到低</option><option value="cost-desc">占用成本从高到低</option></select></label>
        <div><button type="button" onClick={resetFilters}><RotateCcw aria-hidden="true" />重置</button><button className="is-primary" type="submit">查询</button></div>
      </form>

      <div className="table-responsive jd-slow-table-wrap">
        <table className="jd-slow-product-table">
          <thead><tr>
            <th>#</th><th>商品信息</th><th>判断标签</th>
            <th className="numeric"><button className={filters.sort === 'stock-desc' ? 'is-active' : ''} type="button" onClick={() => applySort('stock-desc')}>滞销仓现货<ArrowUpDown aria-hidden="true" /></button></th>
            <th>标签构成</th><th className="numeric">涉及仓库</th>
            <th className="numeric"><button className={filters.sort === 'sales-asc' ? 'is-active' : ''} type="button" onClick={() => applySort('sales-asc')}>全店近30天销量<ArrowDown aria-hidden="true" /></button></th>
            <th className="numeric">最近成交</th>
            <th className="numeric"><button className={filters.sort === 'support-desc' ? 'is-active' : ''} type="button" onClick={() => applySort('support-desc')}>参考支撑天数<ArrowUpDown aria-hidden="true" /></button></th>
            <th className="numeric"><button className={filters.sort === 'cost-desc' ? 'is-active' : ''} type="button" onClick={() => applySort('cost-desc')}>占用成本<ArrowDown aria-hidden="true" /></button></th>
            <th>展开</th>
          </tr></thead>
          <tbody>
            {paged.map((product, index) => <ProductRow product={product} index={start + index} expanded={expanded.has(product.code)} onToggle={toggleProduct} key={product.code} />)}
            {!paged.length ? <tr><td colSpan={11}><div className="empty-state">没有符合当前筛选条件的商品。</div></td></tr> : null}
          </tbody>
        </table>
      </div>

      <div className="jd-slow-table-footer">
        <div className="jd-slow-result-summary">已筛选 <strong>{integer.format(filtered.length)}</strong> 款，滞销仓现货 <strong>{integer.format(filteredStock)}</strong> 件，预计占用成本 <strong>¥{yuan.format(filteredCost)}</strong>。</div>
        <Pagination page={currentPage} pageCount={pageCount} total={filtered.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1) }} />
      </div>
      <SourceAndLogic data={data} />
    </div>
  )
}
